// User invite service
// --------------------
// Admin-facing re-invite for a user who never accepted theirs.
//
// The logged-out equivalent is requestNewInvite, which a stranded invitee runs
// themselves from /welcome and which returns one fixed neutral result so it
// cannot be used to enumerate accounts. This path is different: the caller is
// an admin who already sees the whole user list, so the backend reports the
// real outcome and we can tell the admin why nothing was sent.
//
// The mutation string lives here rather than in services/graphql/mutations.ts
// because that file is codegen output ("auto generated ... will be
// overwritten"); keeping it local prevents it being clobbered.

import { API, graphqlOperation as _graphqlOperation } from "aws-amplify";
import { assertNoTenantIdInVariables } from "@/services/graphql/assertNoTenantId";
import { getGraphData } from "@/utils/transform";

// Reason codes the backend returns. Stable CODES, not display text.
export type ResendInviteReason =
  | "NOT_PENDING"
  | "COOLDOWN"
  | "NOT_FOUND"
  | "FAILED";

export interface ResendInviteResult {
  ok: boolean;
  reason?: ResendInviteReason | null;
}

const RESEND_USER_INVITE = `mutation ResendUserInvite($input: ResendUserInviteInput!) {
  resendUserInvite(input: $input) {
    ok
    reason
  }
}`;

// Force Cognito auth on the call — the backend derives the caller's identity
// and tenant from the JWT.
const graphqlOperation = (query: any, variables?: any) => {
  assertNoTenantIdInVariables(variables);
  return {
    ..._graphqlOperation(query, variables),
    authMode: "AMAZON_COGNITO_USER_POOLS" as const,
  };
};

// Re-send the invite. Never throws — always resolves to a result so the caller
// can render the outcome inline.
export async function resendUserInvite(username: string): Promise<ResendInviteResult> {
  try {
    const response: any = await API.graphql(
      graphqlOperation(RESEND_USER_INVITE, { input: { username } })
    );
    const result = getGraphData(response, "resendUserInvite") as ResendInviteResult | null;
    if (!result) {
      return { ok: false, reason: "FAILED" };
    }
    return result;
  } catch (resendError: any) {
    // Includes the unauthorized case (non-admin, or a cross-tenant target the
    // backend guard refused). Nothing from the rejection reaches the user.
    console.error("resendUserInvite failed");
    return { ok: false, reason: "FAILED" };
  }
}

// User-facing copy per reason code.
export function resendInviteMessage(reason: ResendInviteReason | null | undefined): string {
  switch (reason) {
    case "NOT_PENDING":
      return "This user has already accepted their invite.";
    case "COOLDOWN":
      return "An invite was sent recently. Please wait a few minutes before sending another.";
    case "NOT_FOUND":
      return "We couldn't find that user.";
    default:
      return "Couldn't resend the invite. Please try again.";
  }
}
